import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import WFIRSSQuestion1 from "../Components/WFIRSSQuestion1";
import WFIRSSQuestion2 from "../Components/WFIRSSQuestion2";
import WFIRSSQuestion3 from "../Components/WFIRSSQuestion3";
import WFIRSSQuestion4 from "../Components/WFIRSSQuestion4";
import WFIRSSQuestion5 from "../Components/WFIRSSQuestion5";
import WFIRSSQuestion6 from "../Components/WFIRSSQuestion6";
import WFIRSSQuestion7 from "../Components/WFIRSSQuestion7";
import "./Theme.css";
import "./WFIRSS.css";

const PARAMETERS = ["Never or not at all", "Sometime or somewhat", "Often or much", "Very often or very much", "N/A"];
const PARAM_VALUES = [0, 1, 2, 3, 0];

const SECTION_RANGES = [
  { start: 1, end: 8 },
  { start: 9, end: 19 },
  { start: 20, end: 29 },
  { start: 30, end: 41 },
  { start: 42, end: 46 },
  { start: 47, end: 55 },
  { start: 56, end: 69 },
];

const WFIRSS = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";

  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [componentScores, setComponentScores] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi.fetchWFIRSSQuestions()
      .then((res) => setQuestions(res.data.data || []))
      .catch(() => {});
  }, []);

  const handleOptionChange = (qIndex, value) => {
    setSelectedOptions((prev) => ({ ...prev, [qIndex]: value }));
  };

  const renderRadioInputs = (qIndex) =>
    PARAMETERS.map((param, i) => (
      <div key={i} className="wfirss-radio-row">
        <input
          type="radio"
          name={`wfirs-answer${qIndex}`}
          id={`wfirs-answer${qIndex}-${i}`}
          value={param}
          checked={selectedOptions[qIndex] === param}
          onChange={() => handleOptionChange(qIndex, param)}
        />
        <label htmlFor={`wfirs-answer${qIndex}-${i}`} className="wfirss-radio-label">{param}</label>
      </div>
    ));

  const allAnswered = () => {
    const { start, end } = SECTION_RANGES[step];
    for (let i = start; i <= end; i++) {
      if (!selectedOptions[i]) return false;
    }
    return true;
  };

  const calcComponentScore = () => {
    const { start, end } = SECTION_RANGES[step];
    let score = 0;
    for (let i = start; i <= end; i++) {
      const idx = PARAMETERS.indexOf(selectedOptions[i]);
      score += idx >= 0 ? PARAM_VALUES[idx] : 0;
    }
    return score;
  };

  const handleNext = () => {
    if (!allAnswered()) { setShowAlert(true); return; }
    const newScores = [...componentScores];
    newScores[step] = calcComponentScore();
    setComponentScores(newScores);
    setResetKey((k) => k + 1);
    setStep((s) => s + 1);
  };

  const handleBack = () => setStep((s) => s - 1);

  const handleDone = async () => {
    if (!allAnswered()) { setShowAlert(true); return; }
    setLoading(true);
    const newScores = [...componentScores];
    newScores[step] = calcComponentScore();

    const transformed = {};
    Object.entries(selectedOptions).forEach(([k, v]) => {
      const i = PARAMETERS.indexOf(v);
      transformed[k] = i >= 0 ? i + 1 : 0;
    });

    let total = 0;
    Object.values(selectedOptions).forEach((v) => {
      const i = PARAMETERS.indexOf(v);
      total += i >= 0 ? PARAM_VALUES[i] : 0;
    });

    try {
      const res = await lifestyleApi.logWFIRSS({
        logQuestions: JSON.stringify(transformed),
        score: total,
        componentScore: JSON.stringify(newScores),
      });
      if (String(res.data.code) === "200") {
        const qs = `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;
        navigate(`/wfirs-details?${qs}`);
      }
    } catch (err) {
      // logWFIRSS error
    } finally {
      setLoading(false);
    }
  };

  const QUESTION_COMPONENTS = [
    <WFIRSSQuestion1 key={`q1-${resetKey}`} questions={questions} renderRadioInputs={renderRadioInputs} questionKey={resetKey} />,
    <WFIRSSQuestion2 key={`q2-${resetKey}`} questions={questions} renderRadioInputs={renderRadioInputs} questionKey={resetKey} />,
    <WFIRSSQuestion3 key={`q3-${resetKey}`} questions={questions} renderRadioInputs={renderRadioInputs} questionKey={resetKey} />,
    <WFIRSSQuestion4 key={`q4-${resetKey}`} questions={questions} renderRadioInputs={renderRadioInputs} questionKey={resetKey} />,
    <WFIRSSQuestion5 key={`q5-${resetKey}`} questions={questions} renderRadioInputs={renderRadioInputs} questionKey={resetKey} />,
    <WFIRSSQuestion6 key={`q6-${resetKey}`} questions={questions} renderRadioInputs={renderRadioInputs} questionKey={resetKey} />,
    <WFIRSSQuestion7 key={`q7-${resetKey}`} questions={questions} renderRadioInputs={renderRadioInputs} questionKey={resetKey} />,
  ];

  return (
    <section className="wfirss-page">
      {showAlert && (
        <div className="wfirss-alert-overlay" onClick={() => setShowAlert(false)}>
          <div className="wfirss-alert-box">
            <p>{t("wfirss.answerAll")}</p>
            <button onClick={() => setShowAlert(false)}>{t("common.ok")}</button>
          </div>
        </div>
      )}

      <div className="wfirss-content">
        {QUESTION_COMPONENTS[step]}
      </div>

      <div className="wfirss-nav-btns">
        {step > 0 && (
          <button className="wfirss-back-btn" onClick={handleBack}>{t("common.back")}</button>
        )}
        {step < 6 ? (
          <button className="wfirss-next-btn" onClick={handleNext}>{t("common.next")}</button>
        ) : (
          <button className="wfirss-done-btn" disabled={loading} onClick={handleDone}>
            {loading ? t("common.loading") : t("common.done")}
          </button>
        )}
      </div>
    </section>
  );
};

export default WFIRSS;
